import Vue from 'vue';

// Scripts
import '~scripts/main';

// Styles
import 'styles/pages/events/conference-2024.scss';

// Packages
import lottie from 'lottie-web';
import Swiper from 'swiper';
import { EffectFade, Mousewheel, Navigation as SwiperNavigation, Pagination as SwiperPagination } from 'swiper/modules';
Swiper.use([EffectFade, Mousewheel, SwiperNavigation, SwiperPagination]);

// Core
import { scrollAnimationProgress } from '~scripts/core/animations';
import { BREAKPOINTS } from '~scripts/core/breakpoints';
import { isInViewport } from '~scripts/core/helpers';

// Blocks
import Header from '~scripts/blocks/header';
import Navigation from '~scripts/blocks/navigation';
import '~scripts/blocks/events/forum-2023/modal-video';

// Components
import initTextAnimation from '~scripts/components/text-animation';
import SubscribeForm from '~scripts/components/vue/forms/SubscribeForm.vue';

// Media
const mediaSm = window.matchMedia(`(max-width: ${BREAKPOINTS.smMax})`);

// Animation json
import bannerLottieXs from './conference-2024/banner/lottie/xs.json';
import bannerLottieMd from './conference-2024/banner/lottie/md.json';

//
// HEADER
//
Header();

//
// NAVIGATION
//
Navigation();

//
// BANNER LOTTIE ANIMATION
//
const initBannerAnimation = () => {
  const banner = document.querySelector('.js-banner');
  const bannerAnimationContainer = document.querySelector('.js-lottie-banner');
  const animationData = mediaSm.matches ? bannerLottieXs : bannerLottieMd;

  if (lottie) {
    lottie.destroy();
  }

  try {
    const animation = lottie.loadAnimation({
      container: bannerAnimationContainer,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: animationData,
    });

    animation.addEventListener('loopComplete', () => {
      animation.goToAndPlay(50, true);
    });

    window.addEventListener('scroll', () => {
      animation[isInViewport(bannerAnimationContainer) ? 'play' : 'pause']();
    });
  } catch (error) {
    console.error(error);
    banner.classList.add('banner--background');
  }
};

initBannerAnimation();
mediaSm.addEventListener('change', initBannerAnimation);

//
// INTRO TEXT ANIMATION
//
initTextAnimation('.js-intro-text-animation');

//
// SPEAKERS SLIDER
//
const speakersSliderElement = document.querySelector('.js-speakers-slider');
let speakersSlider = null;

if (speakersSliderElement) {
  const handleSpeakersSliderInit = () => {
    if (speakersSlider) {
      speakersSlider.destroy();
    }

    speakersSlider = new Swiper(speakersSliderElement, {
      centeredSlides: true,
      grabCursor: true,
      loop: true,
      mousewheel: {
        forceToAxis: true,
      },
      navigation: {
        nextEl: '.js-speakers-slider .swiper-button-next',
        prevEl: '.js-speakers-slider .swiper-button-prev',
      },
      slidesPerView: 'auto',
      spaceBetween: mediaSm.matches ? 16 : 32,
      speed: 600,
    });
  };

  handleSpeakersSliderInit();
  mediaSm.addEventListener('change', handleSpeakersSliderInit);
}

//
// BENEFITS
//
// Benefits cards
let stopBenefitsItemsAnimation = [];

const handleBenefitsItemsAnimation = () => {
  const benefitsItems = document.querySelectorAll('.js-benefits-item');

  if (mediaSm.matches) {
    stopBenefitsItemsAnimation.forEach((stop) => stop());
  } else {
    stopBenefitsItemsAnimation = Array.from(benefitsItems, (benefitItem) => {
      return scrollAnimationProgress(benefitItem, {
        getStart(el) {
          const rect = el.getBoundingClientRect();
          return rect.top + window.scrollY - window.innerHeight;
        },
        getEnd(el, start) {
          return start + 100;
        },
      });
    });
  }
};

handleBenefitsItemsAnimation();
mediaSm.addEventListener('change', handleBenefitsItemsAnimation);

// Benefit icons
let stopBenefitItemsAnimation = [];

const handleBenefitItemsAnimation = () => {
  const benefitItems = document.querySelectorAll('.js-benefit-item');

  if (mediaSm.matches) {
    stopBenefitItemsAnimation.forEach((stop) => stop());
  } else {
    stopBenefitItemsAnimation = Array.from(benefitItems, (benefitItem) => {
      return scrollAnimationProgress(benefitItem, {
        getStart(el) {
          const rect = el.getBoundingClientRect();
          return rect.top + window.scrollY - window.innerHeight;
        },
        getEnd(el, start) {
          return start + 300;
        },
      });
    });
  }
};

handleBenefitItemsAnimation();
mediaSm.addEventListener('change', handleBenefitItemsAnimation);

// Subscribe icons
let stopSubscribeScrollAnimation = null;

const handleSubscribePhotoAnimation = () => {
  const SubscribeAnimation = document.querySelector('.js-form-wrapper');

  if (mediaSm.matches) {
    if (stopSubscribeScrollAnimation && typeof stopSubscribeScrollAnimation === 'function') {
      stopSubscribeScrollAnimation();
    }
  } else {
    stopSubscribeScrollAnimation = scrollAnimationProgress(SubscribeAnimation, {
      getStart(el) {
        const rect = el.getBoundingClientRect();
        return rect.top + window.scrollY - window.innerHeight;
      },
      getEnd(el, start) {
        return start + el.offsetHeight;
      },
    });
  }
};

handleSubscribePhotoAnimation();
mediaSm.addEventListener('change', handleSubscribePhotoAnimation);

//
// SUBSCRIBE FORM
//
const subscribeForm = document.querySelector('.js-subscribe-form');

new Vue({
  el: subscribeForm,
  components: {
    SubscribeForm,
  },
});

//
// PLACE SLIDER
//
const placeSliderElement = document.querySelector('.js-place-slider');
let placeSlider = null;

if (placeSliderElement) {
  const handlePlaceSliderInit = () => {
    if (placeSlider) {
      placeSlider.destroy();
    }

    placeSlider = new Swiper(placeSliderElement, {
      centeredSlides: true,
      grabCursor: true,
      initialSlide: 1,
      loop: true,
      mousewheel: {
        forceToAxis: true,
      },
      navigation: {
        nextEl: '.js-place-slider .swiper-button-next',
        prevEl: '.js-place-slider .swiper-button-prev',
      },
      slidesPerView: 'auto',
      spaceBetween: mediaSm.matches ? 16 : 32,
      speed: 600,
    });
  };

  handlePlaceSliderInit();
  mediaSm.addEventListener('change', handlePlaceSliderInit);
}

//
// PHOTO SLIDER
//
const photoSliderElement = document.querySelector('.js-photo-slider');
let photoSlider = null;

if (photoSliderElement) {
  const handlePlaceSliderInit = () => {
    if (photoSlider) {
      photoSlider.destroy();
    }

    photoSlider = new Swiper(photoSliderElement, {
      centeredSlides: true,
      grabCursor: true,
      initialSlide: 1,
      loop: true,
      mousewheel: {
        forceToAxis: true,
      },
      navigation: {
        nextEl: '.js-photo-slider .swiper-button-next',
        prevEl: '.js-photo-slider .swiper-button-prev',
      },
      slidesPerView: 'auto',
      spaceBetween: mediaSm.matches ? 16 : 32,
      speed: 600,
    });
  };

  handlePlaceSliderInit();
  mediaSm.addEventListener('change', handlePlaceSliderInit);
}

//
// CHARITY SLIDER
//
let charitySlider = null;

const charitySliderInit = () => {
  if (!mediaSm.matches && charitySlider !== null) {
    charitySlider.destroy();
  } else {
    charitySlider = new Swiper('.js-charity-mobile-slider', {
      slidesPerView: 'auto',
      speed: 600,
      spaceBetween: 24,
    });
  }
};

charitySliderInit();
mediaSm.addEventListener('change', charitySliderInit);

let stopCharityScrollAnimation = null;

const handleCharityPhotoAnimation = () => {
  const charityAnimation = document.querySelector('.js-charity');

  if (mediaSm.matches) {
    if (stopCharityScrollAnimation && typeof stopCharityScrollAnimation === 'function') {
      stopCharityScrollAnimation();
    }
  } else {
    stopCharityScrollAnimation = scrollAnimationProgress(charityAnimation, {
      getStart(el) {
        const rect = el.getBoundingClientRect();

        return rect.top + window.scrollY - window.innerHeight;
      },
      getEnd(el, start) {
        return start + el.offsetHeight;
      },
    });
  }
};

handleCharityPhotoAnimation();
mediaSm.addEventListener('change', handleCharityPhotoAnimation);

//
// FOOTER
//
const body = document.body;
const main = document.getElementById('main');
const footer = document.getElementById('footer');
const headerHeight = 64;

const scroll = {
  handle() {
    const footerHeight = footer.offsetHeight;

    if (window.scrollY > main.offsetHeight) {
      requestAnimationFrame(() => {
        body.style.paddingBottom = null;
      });

      if (window.innerHeight > footerHeight) {
        footer.style.position = 'static';
      } else {
        footer.style.position = 'relative';
      }
    } else {
      body.style.paddingBottom = `${footerHeight}px`;
      footer.style.position = 'fixed';

      if (window.innerHeight > footerHeight) {
        footer.style.removeProperty('top');
        footer.style.bottom = '0';
      } else {
        footer.style.removeProperty('bottom');
        footer.style.top = `${headerHeight}px`;
      }
    }
  },
  on() {
    this.handle();

    document.addEventListener('scroll', this.handle);
    window.addEventListener('resize', this.handle);
  },
  off() {
    body.style.paddingBottom = null;
    footer.style.position = 'static';

    document.removeEventListener('scroll', this.handle);
    window.removeEventListener('resize', this.handle);
  },
};

// Scroll
window.addEventListener('DOMContentLoaded', () => {
  if (!mediaSm.matches) {
    scroll.on();
  }

  mediaSm.addEventListener('change', () => {
    if (!mediaSm.matches) {
      scroll.on();
    } else {
      scroll.off();
    }
  });
});

window.addEventListener('load', () => {
  if (!mediaSm.matches) {
    scroll.handle();
  }
});

// Animation
let stopFooterAnimation = null;

const handleFooterAnimation = () => {
  if (mediaSm.matches) {
    if (stopFooterAnimation && typeof stopFooterAnimation === 'function') {
      stopFooterAnimation();
    }
  } else {
    stopFooterAnimation = scrollAnimationProgress(body, {
      getStart() {
        const rect = main.getBoundingClientRect();
        return rect.bottom + (window.scrollY - window.innerHeight);
      },
      getEnd(el, start) {
        return start + footer.offsetHeight - headerHeight;
      },
      lerpValue: 1,
    });
  }
};

handleFooterAnimation();
mediaSm.addEventListener('change', handleFooterAnimation);
